import React, { useState, useEffect } from 'react';
import { getEntry } from '../../api/entries';
import { sendToWebhook } from '../../api/client';
import { updateTask } from '../../api/tasks';
import { updateRiskStatus } from '../../api/risks';
import {
  TYPE_COLORS, IMPACT_COLORS, PRIORITY_COLORS, PRIORITY_LABELS,
  TASK_STATUS_LABELS, RISK_STATUS_LABELS,
} from '../../utils/constants';
import { formatDateTime, formatDate, isOverdue } from '../../utils/dates';
import Modal from '../shared/Modal';
import Badge from '../shared/Badge';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';

const TASK_STATUS_OPTIONS = ['pendiente', 'en_progreso', 'bloqueada', 'hecha', 'cancelada'];
const RISK_STATUS_OPTIONS = ['abierto', 'mitigado', 'aceptado', 'cerrado'];

function InlineTaskRow({ task, showToast }) {
  const [status, setStatus] = useState(task.status);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (status === task.status) return;
    setSaving(true);
    try {
      await updateTask(task.id, { status });
      showToast?.('Estado actualizado', 'success');
    } catch {
      showToast?.('Error al actualizar', 'error');
      setStatus(task.status);
    } finally {
      setSaving(false);
    }
  }

  const overdue = isOverdue(task.due_date) && !['hecha', 'cancelada'].includes(status);

  return (
    <div className="entry-task-row">
      <span className="priority-dot" style={{ background: PRIORITY_COLORS[task.priority] }} />
      <div className="entry-task-row__main">
        <span className="entry-task-row__title">{task.title}</span>
        <div className="entry-task-row__meta">
          <span className="meta-chip">{PRIORITY_LABELS[task.priority] || task.priority}</span>
          {task.due_date && (
            <span className={`meta-chip${overdue ? ' danger' : ''}`}>
              📅 {formatDate(task.due_date)}
            </span>
          )}
          {task.assigned_to && <span className="meta-chip">{task.assigned_to}</span>}
        </div>
      </div>
      <div className="entry-task-row__actions">
        <select
          className="filter-select filter-select--inline"
          value={status}
          onChange={e => setStatus(e.target.value)}
          disabled={saving}
        >
          {TASK_STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
          ))}
        </select>
        {status !== task.status && (
          <button className="btn-primary" onClick={save} disabled={saving} style={{ padding: '4px 10px', fontSize: 12 }}>
            {saving ? '…' : 'Guardar'}
          </button>
        )}
      </div>
    </div>
  );
}

function InlineRiskRow({ risk, showToast }) {
  const [status, setStatus] = useState(risk.status);
  const [saving, setSaving] = useState(false);
  const color = IMPACT_COLORS[risk.impact] || 'var(--danger)';

  async function save(val) {
    setSaving(true);
    const prev = status;
    setStatus(val);
    try {
      await updateRiskStatus(risk.id, val);
      showToast?.('Estado actualizado', 'success');
    } catch {
      showToast?.('Error al actualizar', 'error');
      setStatus(prev);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="entry-risk-row">
      <span className="priority-dot" style={{ background: color }} />
      <div className="entry-risk-row__main">
        <span className="entry-risk-row__desc">{risk.description}</span>
        {risk.mitigation && (
          <div className="entry-risk-row__mitigation">
            <strong>Mitigación:</strong> {risk.mitigation}
          </div>
        )}
        <div className="entry-risk-row__meta">
          <span className="meta-chip" style={{ color }}>{risk.impact}</span>
          {risk.probability && <span className="meta-chip">{risk.probability}</span>}
        </div>
      </div>
      <select
        className="filter-select filter-select--inline"
        value={status}
        onChange={e => save(e.target.value)}
        disabled={saving}
      >
        {RISK_STATUS_OPTIONS.map(s => (
          <option key={s} value={s}>{RISK_STATUS_LABELS[s]}</option>
        ))}
      </select>
    </div>
  );
}

function EntryDetailModal({ entryId, onClose, showToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('tasks');
  const [retrying, setRetrying] = useState(false);
  const [retryDone, setRetryDone] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    setLoading(true);
    setError(null);
    setData(null);
    setTab('tasks');
    setRetryDone(false);
    getEntry(entryId)
      .then(res => setData(res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [entryId]);

  async function handleRetry() {
    if (!data) return;
    setRetrying(true);
    try {
      await sendToWebhook(data.content);
      setRetryDone(true);
      showToast?.('Reenviado a N8N', 'success');
    } catch (err) {
      showToast?.(`Error al reenviar: ${err.message}`, 'error');
    } finally {
      setRetrying(false);
    }
  }

  const isClassified = !!data?.classification;
  const typeInfo = data?.classification
    ? (TYPE_COLORS[data.classification.entry_type] || TYPE_COLORS.otro)
    : null;

  const tabs = [
    { id: 'tasks',    label: 'Tareas',   count: data?.tasks?.length },
    { id: 'risks',    label: 'Riesgos',  count: data?.risks?.length },
    { id: 'people',   label: 'Personas', count: data?.people?.length },
    { id: 'artifacts',label: 'Artefactos', count: data?.artifacts?.length },
  ];

  return (
    <Modal isOpen={!!entryId} onClose={onClose} title="Predicado" size="lg">
      {loading && <LoadingState rows={5} />}
      {error && <ErrorState message={error} onRetry={() => {
        setLoading(true);
        getEntry(entryId).then(r => setData(r.data)).catch(e => setError(e.message)).finally(() => setLoading(false));
      }} />}
      {data && (
        <div className="entry-detail">
          <div className="entry-detail__header">
            <div className="entry-detail__meta">
              {isClassified ? (
                <>
                  <Badge label={typeInfo.label} color={typeInfo.color} />
                  {data.classification.project && <span className="meta-chip">{data.classification.project}</span>}
                  <span className="entry-detail__status done">✓ Procesado</span>
                </>
              ) : (
                <span className="entry-detail__status pending">
                  {retryDone ? '⏳ Enviado — procesando…' : '⏳ Sin clasificar'}
                </span>
              )}
              <span className="meta-chip">{formatDateTime(data.created_at)}</span>
            </div>

            {!isClassified && !retryDone && (
              <button
                className="btn-secondary entry-detail__retry"
                onClick={handleRetry}
                disabled={retrying}
              >
                {retrying ? '⟳ Enviando…' : '↩ Reenviar a N8N'}
              </button>
            )}
          </div>

          <div className="entry-detail__content">{data.content}</div>

          <div className="entry-detail__tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`tab-btn${tab === t.id ? ' active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {t.count > 0 && <span className="tab-count">{t.count}</span>}
              </button>
            ))}
          </div>

          <div className="entry-detail__tab-content">
            {tab === 'tasks' && (
              data.tasks?.length > 0 ? (
                <div className="entry-subtab-list">
                  {data.tasks.map(t => (
                    <InlineTaskRow key={t.id} task={t} showToast={showToast} />
                  ))}
                </div>
              ) : <div className="list-empty">Sin tareas en este predicado</div>
            )}

            {tab === 'risks' && (
              data.risks?.length > 0 ? (
                <div className="entry-subtab-list">
                  {data.risks.map(r => (
                    <InlineRiskRow key={r.id} risk={r} showToast={showToast} />
                  ))}
                </div>
              ) : <div className="list-empty">Sin riesgos en este predicado</div>
            )}

            {tab === 'people' && (
              data.people?.length > 0 ? (
                <div className="entry-subtab-list">
                  {data.people.map(p => (
                    <div key={p.id} className="entry-person-row">
                      <div className="person-avatar">
                        {(p.person_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="entry-person-row__name">{p.person_name}</div>
                        {p.role && <div className="entry-person-row__role">{p.role}</div>}
                        {p.context && <div className="entry-person-row__ctx">{p.context}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="list-empty">Sin personas mencionadas</div>
            )}

            {tab === 'artifacts' && (
              data.artifacts?.length > 0 ? (
                <div className="entry-subtab-list">
                  {data.artifacts.map(a => (
                    <div key={a.id} className="entry-artifact-row">
                      <span className="meta-chip">{a.artifact_type}</span>
                      <span className="entry-artifact-row__title">{a.title}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="list-empty">Sin artefactos generados</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

export default EntryDetailModal;
